import React, { FC, useState, useEffect } from 'react';
import { AutoComplete as AutoCompleteAntd } from 'antd';
import styled from 'styled-components';
import { Question, QuestionList } from 'src/store/types';

export interface AutocompleteProps {
  handleQuestionChange: (selectValue: number) => void;
  questions: QuestionList;
  initialTextValue: string;
  className?: string;
}

const FormLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.lightBlue.main};
`;

export const AutoComplete: FC<AutocompleteProps> = ({
  handleQuestionChange,
  questions,
  initialTextValue,
  className,
}) => {
  const [results, setResults] = useState<Question[]>([]);
  const [textValue, setTextValue] = useState<string>(initialTextValue);

  useEffect(() => {
    setTextValue(initialTextValue);
  }, [initialTextValue]);

  const { Option } = AutoCompleteAntd;

  const handleSearch = (valueToSearch: string) => {
    const filterResults = questions.filter(
      (q: Question) =>
        q.question.toLowerCase()?.indexOf(valueToSearch.toLowerCase()) >= 0,
    );
    setResults(filterResults);
  };

  const handleOnSelect = (selectedValue: string) => {
    const questionId = Number(selectedValue);
    if (!Number.isNaN(questionId)) {
      handleQuestionChange(questionId);
    }
    setTextValue('');
  };

  return (
    <AutoCompleteAntd
      onSearch={handleSearch}
      onSelect={handleOnSelect as any}
      placeholder="Search question"
      onChange={(value: string) => setTextValue(value)}
      value={textValue}
      className={className}
      // Filtering is done manually in handleSearch. Disable antd's built-in
      // filtering so it doesn't match against the (now id-based) option value.
      filterOption={false}
    >
      {results.map(({ id, question, formLabel, internalId }: Question) => (
        <Option
          // The option value must be UNIQUE per question. Several questions
          // across forms share the exact same text, and antd resolves a click
          // by value — identical values made every duplicate resolve to the
          // first one, so the choices dropdown showed the wrong question's
          // choices (TH2-216). Keying by id fixes the resolution.
          key={id || internalId}
          value={id ? String(id) : `unsaved-${internalId ?? ''}`}
          disabled={!id}
        >
          {id ? (
            <>
              <FormLabel>{formLabel}</FormLabel>
              {question}
            </>
          ) : (
            '(Unsaved question)'
          )}
        </Option>
      ))}
    </AutoCompleteAntd>
  );
};
